/**
 * Popover2 - Compatibility wrapper for use with Overlay2/Waypoint
 *
 * This version removes arrow rendering (handled by SdfFloatingPane) and Popper-specific
 * code, but maintains the same content structure, styling, and accessibility features.
 */

import classNames from 'classnames';
import { nanoid } from 'nanoid';
import React, { forwardRef, useEffect, useState } from 'react';
import { tabbable } from 'tabbable';
import { FormatHelper } from '@adp-wfn/mdf-core';
import { SdfBox } from '@waypoint/react-components';
import { Placement2 } from './Overlay2';
import logger from '../util/logger';
import vdlPrefix from '../util/vdlPrefix';

type IAriaLive = 'off' | 'assertive' | 'polite';

export interface IPopover2Props {
  /** Aria-live region politeness setting */
  ariaLive?: IAriaLive;

  /** This property is for MDFFilter specifically. A caption for screen readers to read while entering the filter */
  caption?: string;

  children?: React.ReactNode;

  className?: string;

  /** Shows close icon on popover header */
  closeable?: boolean;

  /**
   * When this prop is set, it creates a Popover with a Popover.Content inside
   * passing the children directly to it
   */
  content?: boolean;

  /** Sets the id for popover content. */
  contentId?: string;

  /** header component */
  header?: React.ReactNode;

  /**
   * An html id attribute, necessary for accessibility
   * @type {string|number}
   * @required
   */
  id?: string;

  /** Unused. Here for backward compatibility. */
  placement?: Placement2;

  /** to differentiate between popup and popover usage. */
  popup?: boolean;

  /** Role property added for accessibility */
  role?: string;

  style?: Record<string, any>;

  /** title component */
  title?: React.ReactNode;

  /** Event to handle on popover close action */
  handleClose?: (event) => void;

  /** Event to handle clicks */
  onClick?: (event) => void;

  /** Event to handle key downs */
  onKeyDown?: (event) => void;
}

export const Popover2 = forwardRef<HTMLElement, IPopover2Props>(
  (
    {
      ariaLive,
      caption,
      children,
      className,
      closeable,
      contentId,
      header,
      id,
      popup = false,
      role,
      style,
      title,
      handleClose
    }: IPopover2Props,
    ref: any
  ) => {
    const contentRef = React.useRef<HTMLDivElement>(null);
    const [tabbableCount, setTabbableCount] = useState(0);
    const [isCloseable, setIsCloseable] = useState(!!(closeable && !popup));
    const popoverId = id || 'popover_' + nanoid();

    const classes = {
      [vdlPrefix('popup')]: popup,
      [vdlPrefix('popup__content')]: popup
    };

    const containerClasses = {
      [vdlPrefix('popover__container')]: !popup
    };

    const renderTitle = () => {
      return (
        <div className="popover-title">
          {title}
        </div>
      );
    };

    // close popover when space or enter key is pressed for accessibility
    const handleKeyPress = (event: React.KeyboardEvent) => {
      event.stopPropagation();

      if (event.key === 'Enter' || event.key === ' ') {
        handleClose?.(event);
      }
    };

    useEffect(() => {
      logger('Popover2.useEffect(): contentRef =', contentRef.current);

      if (contentRef.current) {
        const newTabbableCount = tabbable(contentRef.current, { displayCheck: 'none', getShadowRoot: true, includeContainer: false })?.length ?? 0;
        setTabbableCount(newTabbableCount);
        logger('Popover2.useEffect(): tabbableCount =', tabbableCount);
        setIsCloseable(newTabbableCount === 0 ? !!(closeable && !popup) : (isCloseable || newTabbableCount > 0));
      }
    });

    logger('Popover2(): isCloseable =', isCloseable);

    return (
      <SdfBox
        ref={ref}
        className={classNames('mdf', 'vdl-overlay-container', className, classes)}
        id={popoverId}
        role={role || 'tooltip'}
        style={style}
        tabIndex={0}
        variant="shadowed"
      >
        <span className="mdf-sr-only" role="dialog" aria-label={caption} aria-modal={true} tabIndex={0}></span>
        <div className={classNames(containerClasses)} aria-live={ariaLive || 'polite'}>
          {header ? header : (<div className={vdlPrefix('popover-header')}>
            {title ? renderTitle() : null}
            {isCloseable ? (<div className={vdlPrefix('popover__close')} onClick={(evt) => handleClose?.(evt)} role="button" aria-label={FormatHelper.formatMessage('@@Close')} tabIndex={0} onKeyUp={handleKeyPress}>
              <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
                <path d="M0 0h24v24H0V0z" fill="none" />
                <path d="M18.3 5.71c-.39-.39-1.02-.39-1.41 0L12 10.59 7.11 5.7c-.39-.39-1.02-.39-1.41 0-.39.39-.39 1.02 0 1.41L10.59 12 5.7 16.89c-.39.39-.39 1.02 0 1.41.39.39 1.02.39 1.41 0L12 13.41l4.89 4.89c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41L13.41 12l4.89-4.89c.38-.38.38-1.02 0-1.4z" />
              </svg>
            </div>) : null}
          </div>)}
          <div className={!popup ? vdlPrefix('popover-content') : ''} id={contentId} ref={contentRef}>
            {children}
          </div>
        </div>
      </SdfBox>
    );
  }
);

Popover2.displayName = 'Popover2';
